import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Use relative /api path when served from a custom domain
function getApiBase() {
  if (!BACKEND_URL) return '/api';
  try {
    const configuredOrigin = new URL(BACKEND_URL).origin;
    const currentOrigin = window.location.origin;
    if (configuredOrigin === currentOrigin) return `${BACKEND_URL}/api`;
  } catch (e) {
    console.warn('Failed to parse REACT_APP_BACKEND_URL, falling back to relative /api path:', e);
  }
  return '/api';
}

const API = getApiBase();

/**
 * Platform API client for use outside of TenantContext.
 * Allows explicit tenant slug per request or no tenant header at all.
 */
const platformAxios = axios.create({
  baseURL: API,
  withCredentials: true,
  // 45s upper bound — long enough for cold-starts on cold prod containers,
  // short enough that a hung backend surfaces as an error toast rather than
  // a forever-spinning button.
  timeout: 45000,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const platformApi = {
  /**
   * Make a GET request
   * @param {string} url - API endpoint
   * @param {string} [tenantSlug] - Optional tenant slug for X-Tenant-Slug header
   */
  get: async (url, tenantSlug = null) => {
    const config = {};
    if (tenantSlug) {
      config.headers = { 'X-Tenant-Slug': tenantSlug };
    }
    return platformAxios.get(url, config);
  },

  /**
   * Make a POST request
   * @param {string} url - API endpoint
   * @param {object} data - Request body
   * @param {string} [tenantSlug] - Optional tenant slug for X-Tenant-Slug header
   */
  post: async (url, data, tenantSlug = null) => {
    const config = {};
    if (tenantSlug) {
      config.headers = { 'X-Tenant-Slug': tenantSlug };
    }
    return platformAxios.post(url, data, config);
  },

  /**
   * Make a PUT request
   * @param {string} url - API endpoint
   * @param {object} data - Request body
   * @param {string} [tenantSlug] - Optional tenant slug for X-Tenant-Slug header
   */
  put: async (url, data, tenantSlug = null) => {
    const config = {};
    if (tenantSlug) {
      config.headers = { 'X-Tenant-Slug': tenantSlug };
    }
    return platformAxios.put(url, data, config);
  },

  /**
   * Make a DELETE request
   * @param {string} url - API endpoint
   * @param {string} [tenantSlug] - Optional tenant slug for X-Tenant-Slug header
   */
  delete: async (url, tenantSlug = null) => {
    const config = {};
    if (tenantSlug) {
      config.headers = { 'X-Tenant-Slug': tenantSlug };
    }
    return platformAxios.delete(url, config);
  }
};

export default platformApi;
